import { useState } from 'react';
import { useAppStore } from '@/state/store';
import { exportGLB } from '@/lib/export/exportGLB';
import { exportSTL } from '@/lib/export/exportSTL';
import { exportOBJ } from '@/lib/export/exportOBJ';
import { exportThumbnail } from '@/lib/export/exportThumbnail';

function slug(name: string): string {
  return name.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '') || 'mark_lumber_bat';
}

export function ActionButtons() {
  const model = useAppStore((s) => s.model);
  const geometry = useAppStore((s) => s.geometry);
  const meshRef = useAppStore((s) => s.meshRef);
  const canvasRef = useAppStore((s) => s.canvasRef);
  const autoDetect = useAppStore((s) => s.autoDetect);
  const generateGeometry = useAppStore((s) => s.generateGeometry);
  const reset = useAppStore((s) => s.reset);
  const pushStatus = useAppStore((s) => s.pushStatus);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const hasModel = !!model;
  const hasMesh = !!geometry && !!meshRef;
  const baseName = model ? slug(model.modelName) : 'mark_lumber_bat';

  async function runExport(kind: string, fn: () => void | Promise<void>) {
    if (!hasMesh || !meshRef) {
      pushStatus('warning', 'Generate the 3D model before exporting.');
      return;
    }
    setBusyAction(kind);
    try {
      await fn();
      pushStatus('success', `Exported ${kind}.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown export error.';
      pushStatus('error', `Failed to export ${kind}: ${message}`);
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <div>
      <div className="sidebar-section-label">Actions</div>
      <div className="btn-group">
        <button className="btn" disabled={!hasModel} onClick={() => autoDetect()}>
          Auto Detect
        </button>
        <button className="btn btn-primary" disabled={!hasModel} onClick={() => generateGeometry()}>
          Generate
        </button>
        <button className="btn btn-ghost" disabled={!hasModel} onClick={() => reset()}>
          Reset
        </button>
      </div>

      <div className="sidebar-section-label" style={{ marginTop: 18 }}>
        Export
      </div>
      <div className="btn-group">
        <button
          className="btn"
          disabled={!hasMesh || busyAction !== null}
          onClick={() =>
            runExport('GLB', () => (meshRef ? exportGLB(meshRef, `${baseName}.glb`) : undefined))
          }
        >
          {busyAction === 'GLB' ? 'Exporting…' : 'Download GLB'}
        </button>
        <button
          className="btn"
          disabled={!hasMesh || busyAction !== null}
          onClick={() =>
            runExport('STL', () => (meshRef ? exportSTL(meshRef, `${baseName}.stl`) : undefined))
          }
        >
          {busyAction === 'STL' ? 'Exporting…' : 'Download STL'}
        </button>
        <button
          className="btn"
          disabled={!hasMesh || busyAction !== null}
          onClick={() =>
            runExport('OBJ', () => (meshRef ? exportOBJ(meshRef, `${baseName}.obj`) : undefined))
          }
        >
          {busyAction === 'OBJ' ? 'Exporting…' : 'Download OBJ'}
        </button>
        <button
          className="btn"
          disabled={!hasMesh || !canvasRef || busyAction !== null}
          onClick={() =>
            runExport('PNG Thumbnail', () =>
              canvasRef ? exportThumbnail(canvasRef, `${baseName}_thumbnail.png`) : undefined,
            )
          }
        >
          {busyAction === 'PNG Thumbnail' ? 'Exporting…' : 'Download PNG Thumbnail'}
        </button>
      </div>
    </div>
  );
}
